<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import moment from 'moment-jalaali'
import DatePicker from 'vue3-persian-datetime-picker'
import { useLocalization } from '@/composables/useLocalization'

const { __ } = useLocalization()

const props = defineProps<{
  settingsUrl: string
  saveUrl: string
  disableUrl: string
  enableUrl: string
  restartUrl: string
  canEdit: boolean
  logsUrl: string
}>()

type ScheduleMode = 'always' | 'daily_window'

interface SettingsForm {
  enabled: boolean
  schedule_mode: ScheduleMode
  window_start: string
  window_end: string
  starts_at: string
  timezone: string
  tf_switch_delay_seconds: number
  tf_load_wait_seconds: number
  element_wait_seconds: number
  login_stagger_seconds: number
  max_cycle_seconds: number
}

const TUNING_DEFAULTS = {
  tf_switch_delay_seconds: 8,
  tf_load_wait_seconds: 10,
  element_wait_seconds: 10,
  login_stagger_seconds: 8,
  max_cycle_seconds: 1200,
}

interface RuntimeStatus {
  active_now: boolean
  state: string
  message: string
  next_run_at: string | null
}

const loading = ref(true)
const saving = ref(false)
const disabling = ref(false)
const enabling = ref(false)
const restarting = ref(false)
const errorMessage = ref('')
const successMessage = ref('')
const runtime = ref<RuntimeStatus | null>(null)

const form = ref<SettingsForm>({
  enabled: true,
  schedule_mode: 'always',
  window_start: '08:00',
  window_end: '22:00',
  starts_at: '',
  timezone: 'Asia/Tehran',
  ...TUNING_DEFAULTS,
})

const tuningFields: { key: keyof typeof TUNING_DEFAULTS; label: string; hint?: string; min: number; max: number }[] = [
  { key: 'tf_switch_delay_seconds', label: __('Timeframe switch delay (s)'), min: 0, max: 120 },
  { key: 'tf_load_wait_seconds', label: __('Timeframe load wait (s)'), min: 3, max: 120 },
  { key: 'element_wait_seconds', label: __('Element wait (s)'), min: 3, max: 120 },
  { key: 'login_stagger_seconds', label: __('Login stagger (s)'), min: 0, max: 120 },
  { key: 'max_cycle_seconds', label: __('Max cycle time (s)'), hint: __('Above this the running cycle is aborted and restarted.'), min: 300, max: 3600 },
]

/** Jalali string shown in the picker; API still stores Gregorian `YYYY-MM-DDTHH:mm`. */
const startsAtJalali = ref('')

function syncStartsAtJalaliFromForm(): void {
  startsAtJalali.value = form.value.starts_at
    ? moment(form.value.starts_at, 'YYYY-MM-DDTHH:mm').format('jYYYY/jMM/jDD HH:mm')
    : ''
}

function onStartsAtJalaliChange(value: string | null): void {
  const next = (value || '').trim()
  startsAtJalali.value = next
  form.value.starts_at = next
    ? moment(next, 'jYYYY/jMM/jDD HH:mm').format('YYYY-MM-DDTHH:mm')
    : ''
}

function formatNextRun(iso: string | null | undefined): string {
  if (!iso) {
    return '—'
  }
  return moment(iso).utcOffset(210).format('jYYYY/jMM/jDD HH:mm')
}

const timezones = [
  'Asia/Tehran',
]

function csrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? ''
}

const statusTone = computed(() => {
  if (!form.value.enabled) return 'disabled'
  if (!runtime.value) return 'unknown'
  if (runtime.value.active_now) return 'live'
  if (runtime.value.state === 'scheduled') return 'scheduled'
  return 'paused'
})

const statusLabel = computed(() => {
  if (!form.value.enabled) return __('Disabled')
  return runtime.value?.message || __('Loading…')
})

async function load() {
  loading.value = true
  errorMessage.value = ''
  try {
    const res = await fetch(props.settingsUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
    if (!res.ok) throw new Error(res.statusText)
    const data = await res.json()
    const s = data.settings || {}
    form.value = {
      enabled: Boolean(s.enabled),
      schedule_mode: s.schedule_mode === 'daily_window' ? 'daily_window' : 'always',
      window_start: s.window_start || '08:00',
      window_end: s.window_end || '22:00',
      starts_at: s.starts_at || '',
      timezone: s.timezone || 'Asia/Tehran',
      tf_switch_delay_seconds: Number(s.tf_switch_delay_seconds ?? TUNING_DEFAULTS.tf_switch_delay_seconds),
      tf_load_wait_seconds: Number(s.tf_load_wait_seconds ?? TUNING_DEFAULTS.tf_load_wait_seconds),
      element_wait_seconds: Number(s.element_wait_seconds ?? TUNING_DEFAULTS.element_wait_seconds),
      login_stagger_seconds: Number(s.login_stagger_seconds ?? TUNING_DEFAULTS.login_stagger_seconds),
      max_cycle_seconds: Number(s.max_cycle_seconds ?? TUNING_DEFAULTS.max_cycle_seconds),
    }
    syncStartsAtJalaliFromForm()
    runtime.value = data.runtime || null
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to load settings')
  } finally {
    loading.value = false
  }
}

async function save() {
  if (!props.canEdit) return
  saving.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const res = await fetch(props.saveUrl, {
      method: 'PUT',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
      body: JSON.stringify({
        ...form.value,
        starts_at: form.value.starts_at || null,
      }),
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || res.statusText)
    }
    runtime.value = data.runtime || null
    if (data.settings) {
      form.value.enabled = Boolean(data.settings.enabled)
      form.value.starts_at = data.settings.starts_at || ''
      syncStartsAtJalaliFromForm()
    }
    successMessage.value = __('Settings saved')
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to save settings')
  } finally {
    saving.value = false
  }
}

async function disableNow() {
  if (!props.canEdit || !form.value.enabled) return
  if (!window.confirm(__('Disable collector now? Running capture cycles will stop.'))) {
    return
  }
  disabling.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const res = await fetch(props.disableUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || res.statusText)
    }
    form.value.enabled = false
    runtime.value = data.runtime || null
    successMessage.value = data.message || __('Collector disabled.')
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to disable collector')
  } finally {
    disabling.value = false
  }
}

async function enableNow() {
  if (!props.canEdit || form.value.enabled) return
  enabling.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const res = await fetch(props.enableUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || res.statusText)
    }
    form.value.enabled = true
    runtime.value = data.runtime || null
    successMessage.value = data.message || __('Collector enabled.')
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to enable collector')
  } finally {
    enabling.value = false
  }
}

async function toggleEnabled() {
  if (!props.canEdit || enabling.value || disabling.value) return
  if (form.value.enabled) {
    await disableNow()
    return
  }
  await enableNow()
}

async function restartService() {
  if (!props.canEdit) return
  if (!window.confirm(__('Restart collector service now? The current cycle may be interrupted.'))) {
    return
  }
  restarting.value = true
  errorMessage.value = ''
  successMessage.value = ''
  try {
    const res = await fetch(props.restartUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        Accept: 'application/json',
        'X-CSRF-TOKEN': csrfToken(),
      },
    })
    const data = await res.json().catch(() => ({}))
    if (!res.ok) {
      throw new Error(data.message || data.output || res.statusText)
    }
    runtime.value = data.runtime || null
    successMessage.value = data.message || __('Collector service restarted.')
  } catch (error: any) {
    errorMessage.value = error?.message || __('Failed to restart collector service')
  } finally {
    restarting.value = false
  }
}

function selectMode(mode: ScheduleMode) {
  if (!props.canEdit) return
  form.value.schedule_mode = mode
}

onMounted(load)
</script>

<template>
  <div class="scraper-settings-app min-w-0 space-y-4 md:space-y-5">
    <header class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5 md:p-6">
      <div class="panel-glass-content flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div class="min-w-0 space-y-2">
          <span class="panel-glass-chip inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold">
            <span class="relative flex size-2 shrink-0 overflow-hidden rounded-full">
              <span
                class="absolute inline-flex size-full animate-ping rounded-full opacity-60"
                :class="{
                  'bg-emerald-400': statusTone === 'live',
                  'bg-amber-400': statusTone === 'scheduled' || statusTone === 'paused',
                  'bg-zinc-400': statusTone === 'disabled' || statusTone === 'unknown',
                }"
              />
              <span
                class="relative inline-flex size-2 rounded-full"
                :class="{
                  'bg-emerald-500': statusTone === 'live',
                  'bg-amber-500': statusTone === 'scheduled' || statusTone === 'paused',
                  'bg-zinc-400': statusTone === 'disabled' || statusTone === 'unknown',
                }"
              />
            </span>
            {{ __('Collector') }}
          </span>
          <div>
            <h1 class="text-lg font-semibold tracking-tight text-zinc-900 dark:text-white sm:text-xl">
              {{ __('Collector schedule') }}
            </h1>
            <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400 sm:text-sm">
              {{ statusLabel }}
            </p>
          </div>
        </div>

        <div class="flex flex-wrap gap-2 sm:justify-end">
          <button
            v-if="!form.enabled"
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-emerald-500/30 bg-emerald-500/10 px-4 py-2.5 text-sm font-semibold text-emerald-800 transition hover:bg-emerald-500/20 dark:text-emerald-200"
            :disabled="!canEdit || enabling || loading"
            @click="enableNow"
          >
            {{ enabling ? __('Enabling…') : __('Enable collector') }}
          </button>

          <button
            v-if="form.enabled"
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-rose-500/30 bg-rose-500/10 px-4 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-500/20 dark:text-rose-200"
            :disabled="!canEdit || disabling || loading"
            @click="disableNow"
          >
            {{ disabling ? __('Disabling…') : __('Disable collector') }}
          </button>

          <button
            type="button"
            class="inline-flex items-center justify-center rounded-full border border-amber-500/30 bg-amber-500/10 px-4 py-2.5 text-sm font-semibold text-amber-800 transition hover:bg-amber-500/20 dark:text-amber-200"
            :disabled="!canEdit || restarting || loading"
            @click="restartService"
          >
            {{ restarting ? __('Restarting…') : __('Restart collector service') }}
          </button>

          <a
            :href="logsUrl"
            class="panel-glass-btn inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-medium"
          >
            {{ __('View collector log') }}
          </a>
        </div>
      </div>
    </header>

    <p v-if="loading" class="text-sm text-zinc-500">{{ __('Loading…') }}</p>

    <template v-else>
      <section class="panel-glass rounded-[1.75rem] p-4 sm:rounded-[2rem] sm:p-5 md:p-6">
        <div class="panel-glass-content space-y-5">
          <div class="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Enable collector') }}</p>
              <p class="mt-1 text-xs text-zinc-500 dark:text-zinc-400">
                {{ __('When disabled, capture cycles exit immediately.') }}
              </p>
            </div>
            <button
              type="button"
              class="panel-glass-btn rounded-full px-4 py-2 text-sm font-semibold"
              :class="{ 'panel-glass-btn--active': form.enabled }"
              :disabled="!canEdit || enabling || disabling"
              @click="toggleEnabled"
            >
              {{ enabling ? __('Enabling…') : disabling ? __('Disabling…') : form.enabled ? __('Enabled') : __('Disabled') }}
            </button>
          </div>

          <div class="grid gap-3 md:grid-cols-2">
            <button
              type="button"
              class="panel-glass-card rounded-2xl p-4 text-left transition"
              :class="{ 'panel-glass-card--active': form.schedule_mode === 'always' }"
              :disabled="!canEdit"
              @click="selectMode('always')"
            >
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Always on') }}</p>
              <p class="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                {{ __('Run without an end date — 24/7 while enabled.') }}
              </p>
            </button>

            <button
              type="button"
              class="panel-glass-card rounded-2xl p-4 text-left transition"
              :class="{ 'panel-glass-card--active': form.schedule_mode === 'daily_window' }"
              :disabled="!canEdit"
              @click="selectMode('daily_window')"
            >
              <p class="text-sm font-semibold text-zinc-900 dark:text-white">{{ __('Daily time window') }}</p>
              <p class="mt-2 text-xs leading-relaxed text-zinc-500 dark:text-zinc-400">
                {{ __('Only capture between selected hours each day.') }}
              </p>
            </button>
          </div>

          <div
            v-if="form.schedule_mode === 'daily_window'"
            dir="ltr"
            class="panel-glass-inset grid gap-4 rounded-2xl p-4 sm:grid-cols-2"
          >
            <label class="block space-y-1.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{{ __('From') }}</span>
              <input
                v-model="form.window_start"
                type="time"
                class="w-full rounded-xl border border-zinc-200/80 bg-white/80 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100"
                :disabled="!canEdit"
              >
            </label>
            <label class="block space-y-1.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{{ __('To') }}</span>
              <input
                v-model="form.window_end"
                type="time"
                class="w-full rounded-xl border border-zinc-200/80 bg-white/80 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100"
                :disabled="!canEdit"
              >
            </label>
          </div>

          <div dir="ltr" class="grid gap-4 md:grid-cols-2">
            <div class="block space-y-1.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{{ __('Start from (optional)') }}</span>
              <div class="starts-at-picker relative z-[60]">
                <DatePicker
                  :model-value="startsAtJalali"
                  type="datetime"
                  format="jYYYY/jMM/jDD HH:mm"
                  display-format="jYYYY/jMM/jDD HH:mm"
                  :clearable="true"
                  :disabled="!canEdit"
                  compact-time
                  popover
                  append-to="body"
                  locale="fa"
                  color="rgb(30, 136, 229)"
                  input-class="w-full rounded-xl border border-zinc-200/80 bg-white/80 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100"
                  :placeholder="__('Jalali date & time (Tehran)')"
                  @update:model-value="onStartsAtJalaliChange"
                />
              </div>
              <span class="text-[11px] text-zinc-500">{{ __('Leave empty to start immediately when enabled.') }}</span>
            </div>

            <label class="block space-y-1.5">
              <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{{ __('Timezone') }}</span>
              <select
                v-model="form.timezone"
                class="w-full rounded-xl border border-zinc-200/80 bg-white/80 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100"
                :disabled="!canEdit"
              >
                <option v-for="tz in timezones" :key="tz" :value="tz">{{ tz }}</option>
              </select>
            </label>
          </div>

          <fieldset class="space-y-3 rounded-2xl border border-zinc-200/70 p-4 dark:border-zinc-700/70">
            <legend class="px-2 text-[10px] font-bold uppercase tracking-wider text-zinc-400">
              {{ __('Capture tuning') }}
            </legend>
            <div class="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <label v-for="f in tuningFields" :key="f.key" class="block space-y-1.5">
                <span class="text-[10px] font-bold uppercase tracking-wider text-zinc-400">{{ f.label }}</span>
                <input
                  v-model.number="form[f.key]"
                  type="number"
                  :min="f.min"
                  :max="f.max"
                  class="w-full rounded-xl border border-zinc-200/80 bg-white/80 px-3 py-2 text-sm text-zinc-900 dark:border-zinc-700 dark:bg-zinc-900/80 dark:text-zinc-100"
                  :disabled="!canEdit"
                />
                <span v-if="f.hint" class="text-[11px] text-zinc-500">{{ f.hint }}</span>
              </label>
            </div>
          </fieldset>

          <div
            v-if="runtime?.next_run_at"
            class="rounded-2xl border border-amber-500/20 bg-amber-500/10 px-4 py-3 text-sm text-amber-800 dark:text-amber-200"
          >
            {{ __('Next allowed run') }}:
            <span class="font-mono">{{ formatNextRun(runtime.next_run_at) }}</span>
          </div>

          <p v-if="!canEdit" class="text-sm text-amber-600 dark:text-amber-300">
            {{ __('You need admin permission to change collector settings.') }}
          </p>

          <p v-if="errorMessage" class="text-sm text-rose-600 dark:text-rose-300">{{ errorMessage }}</p>
          <p v-if="successMessage" class="text-sm text-emerald-600 dark:text-emerald-300">{{ successMessage }}</p>

          <div class="flex flex-wrap gap-3">
            <button
              type="button"
              class="panel-glass-btn rounded-full px-5 py-2.5 text-sm font-semibold"
              :class="{ 'panel-glass-btn--active': saving }"
              :disabled="!canEdit || saving"
              @click="save"
            >
              {{ saving ? __('Saving…') : __('Save settings') }}
            </button>
            <button
              type="button"
              class="panel-glass-btn rounded-full px-5 py-2.5 text-sm"
              :disabled="loading"
              @click="load"
            >
              {{ __('Refresh') }}
            </button>
          </div>
        </div>
      </section>
    </template>
  </div>
</template>

<style>
/* Keep picker above Nova glass panels; restore action buttons in dark theme. */
.starts-at-picker .vpd-main {
  width: 100%;
}

.vpd-wrapper.vpd-is-popover {
  z-index: 80;
}

.vpd-actions button {
  color: #111827 !important;
}

.vpd-next svg,
.vpd-prev svg {
  display: inline;
}
</style>
